/**
 * Squad leaderboard pagination math (Story 9).
 *
 * The squad leaderboard is fetched page-by-page via `useInfiniteQuery`. These pure
 * helpers keep the page-size constant and the "which page holds this rank" math out
 * of the hook/component so they can be unit-tested in isolation.
 */

/** Rows per page for the paginated squad leaderboard. Mirrors the server default. */
export const SQUAD_LEADERBOARD_PAGE_SIZE = 50;

/**
 * 1-based page index that contains the row at `rank` for a given page size.
 *
 * Used by jump-to-me: tapping the self-standing bar fetches the viewer's page
 * directly (`ceil(rank / pageSize)`) instead of walking pages sequentially.
 * Ranks below 1 clamp to page 1.
 */
export function pageFromRank(rank: number, pageSize: number = SQUAD_LEADERBOARD_PAGE_SIZE): number {
  if (!Number.isFinite(rank) || rank < 1) return 1;
  if (!Number.isFinite(pageSize) || pageSize < 1) return 1;
  return Math.ceil(rank / pageSize);
}

/** Distance (px) from the bottom at which scroll-end auto-load triggers. */
export const SCROLL_END_THRESHOLD_PX = 240;

interface ScrollMetrics {
  contentOffsetY: number;
  layoutHeight: number;
  contentHeight: number;
}

/**
 * True when the scroll position is within `threshold` px of the content bottom.
 * Pure so the auto-load trigger in `GroupDetailView` is unit-testable without a
 * real scroll event. Guards against the initial layout pass where content height
 * is still 0.
 */
export function isNearScrollEnd(
  { contentOffsetY, layoutHeight, contentHeight }: ScrollMetrics,
  threshold: number = SCROLL_END_THRESHOLD_PX,
): boolean {
  if (contentHeight <= 0 || layoutHeight <= 0) return false;
  return contentOffsetY + layoutHeight >= contentHeight - threshold;
}
